import { ChannelsNames, ChannelsDeploymentStatuses, ChannelsWithDeployment } from './Channels';
import { ChannelsFeatures } from './ChannelsFeatures';
export class ChannelsService {
    static $inject = ['ConfigurationsService', 'GatewayStateConfigurationsService', 'ApplicationService2', 'MessagingHubService', 'TenantService'];
    constructor(ConfigurationsService, GatewayStateConfigurationsService, ApplicationService2, MessagingHubService, TenantService) {
        this.ConfigurationsService = ConfigurationsService;
        this.GatewayStateConfigurationsService = GatewayStateConfigurationsService;
        this.ApplicationService2 = ApplicationService2;
        this.MessagingHubService = MessagingHubService;
        this.TenantService = TenantService;
        this.useConfigurationApplicationGet = false;
        this.isWorkplaceActive = false;
    }
    setApplication(application) {
        this.application = application;
    }
    async updateChannelConfigurations(channel) {
        return await this.GatewayStateConfigurationsService.set(this.getChannelId(channel.name), channel.configurations);
    }
    async getAllChannels(useConfigurationsApplication = false, useGatewayConfigurationsEndpoint = false) {
        let channelsConfigurations = undefined;
        if (useGatewayConfigurationsEndpoint) {
            channelsConfigurations = await this.retrieveChannels(useConfigurationsApplication);
        }
        else {
            channelsConfigurations = await this.retrieveChannelsIndividually(useConfigurationsApplication);
        }
        if (channelsConfigurations == undefined) {
            return channelsConfigurations;
        }
        return channelsConfigurations.filter(c => c.configurations !== undefined);
    }
    async retrieveChannelsIndividually(useConfigurationsApplication = false) {
        const channelsPromises = Object.values(ChannelsNames).map(async (cn) => {
            const channelConfigurations = await this.getChannelConfigurations(cn, useConfigurationsApplication);
            return {
                name: cn,
                configurations: channelConfigurations,
            };
        });
        const channels = await Promise.all(channelsPromises);
        return channels;
    }
    async retrieveChannels(useConfigurationsApplication = false) {
        try {
            const newChannels = await this.ConfigurationsService.getGateways(useConfigurationsApplication);
            const newChannelsConfig = Object.values(ChannelsNames).map(cn => {
                const cnPostmaster = this.getChannelId(cn);
                const newChannelConfiguration = newChannels[cnPostmaster];
                return {
                    name: cn,
                    configurations: newChannelConfiguration,
                };
            });
            return newChannelsConfig;
        }
        catch (ex) {
            if (ex.code != 67) {
                throw ex;
            }
            return;
        }
    }
    async getChannel(channelName, useConfigurationsApplication = false) {
        const channelConfigurations = await this.getChannelConfigurations(channelName, useConfigurationsApplication);
        const channel = [{
                name: channelName,
                configurations: !channelConfigurations ? {} : channelConfigurations,
            }];
        return channel;
    }
    async init(application = undefined) {
        if (application?.shortName !== this.application?.shortName) {
            this.activationStatuses = undefined;
            this.deploymentStatuses = undefined;
            this.application = application;
        }
        await this.checkFeatures();
        await this.initIsWorkplaceActive();
    }
    async checkTokenIsValid(channelName) {
        const commandResponse = await this.MessagingHubService.processCommand({
            to: this.getChannelId(channelName),
            method: 'get',
            uri: '/validate-page-token',
        });
        const { resource } = this.MessagingHubService.ensureCommandSuccess(commandResponse);
        return resource;
    }
    async checkFeatures() {
        this.channelsUsingConfigurationsService = await ChannelsFeatures.getChannelsUsingConfigurationsService();
        this.useConfigurationApplicationGet = await ChannelsFeatures.isConfigurationsDomainOnChannelsOperationsGetEnabled();
    }
    setChannelsActivationStatuses() {
        this.activationStatuses = {};
        Object.values(ChannelsNames)
            .removeItem(ChannelsNames.Facebook)
            .forEach((channel) => {
            this.activationStatuses[channel] = this.isChannelActive(channel);
        });
    }
    async setChannelsDeploymentStatuses() {
        const deploymentStatusPromises = [];
        this.deploymentStatuses = {};
        const promiseFunc = async (channel) => {
            const status = await this.GetChannelDeploymentStatus(channel);
            this.deploymentStatuses[channel] = status;
        };
        for (const channel of ChannelsWithDeployment) {
            deploymentStatusPromises.push(promiseFunc(channel));
        }
        await Promise.all(deploymentStatusPromises);
    }
    async getFacebookAppInformation(channelName, subsidiary) {
        const uri = `/facebook-app-information${subsidiary ? `/${subsidiary}` : ''}`;
        const commandResponse = await this.MessagingHubService.processCommand({
            to: this.getChannelId(channelName),
            method: 'get',
            uri: uri,
        });
        const { resource } = this.MessagingHubService.ensureCommandSuccess(commandResponse);
        return resource;
    }
    isChannelActive(channelName) {
        if (channelName === ChannelsNames.Facebook) {
            channelName = ChannelsNames.Messenger;
        }
        if (channelName == ChannelsNames.Mailgun ||
            channelName == ChannelsNames.Blipchat) {
            return true;
        }
        return this.isChannelActiveByConfiguration(channelName);
    }
    async getSubsidiary(channelName) {
        const subsidiaryFromConfiguration = this.getSubsidiaryByConfiguration(channelName);
        if (subsidiaryFromConfiguration) {
            return subsidiaryFromConfiguration;
        }
        const subsidiaryFromTenant = await this.getSubsidiaryByTenant();
        if (subsidiaryFromTenant) {
            return subsidiaryFromTenant;
        }
        return undefined;
    }
    async GetChannelDeploymentStatus(channelName) {
        try {
            const commandResponse = await this.MessagingHubService.processCommand({
                to: this.getChannelId(channelName),
                method: 'get',
                uri: '/deployment-status',
            });
            const { resource: deploymentStatus } = this.MessagingHubService.ensureCommandSuccess(commandResponse);
            return deploymentStatus;
        }
        catch {
            return ChannelsDeploymentStatuses.Unknown;
        }
    }
    /**
     * @todo Remove it when you make WorkchatController extend ChannelBaseController
     */
    async initIsWorkplaceActive() {
        const workplaceConfigurations = await this.getChannelConfigurations(ChannelsNames.Workplace, this.useConfigurationApplicationGet);
        this.isWorkplaceActive = Boolean(workplaceConfigurations &&
            workplaceConfigurations.IsChannelActive &&
            workplaceConfigurations.IsChannelActive.toLowerCase() === 'true');
    }
    async getChannelConfigurations(channelName, useConfigurationsApplication = false) {
        try {
            return await this.ConfigurationsService.get(this.getChannelId(channelName), undefined, useConfigurationsApplication);
        }
        catch (ex) {
            if (ex.code != 67) {
                throw ex;
            }
            return;
        }
    }
    isChannelActiveByConfiguration(channelName) {
        if (channelName === ChannelsNames.Workplace) {
            return this.isWorkplaceActive;
        }
        const channel = this.ApplicationService2.getChannel(channelName, this.application);
        return Boolean(channel &&
            channel.configurations &&
            channel.configurations.IsChannelActive &&
            channel.configurations.IsChannelActive.toLowerCase() === 'true');
    }
    getChannelId(channelName) {
        return `postmaster@${channelName}.gw.msging.net`;
    }
    getSubsidiaryByConfiguration(channelName) {
        const channel = this.ApplicationService2.getChannel(channelName, this.application);
        return channel?.configurations?.Subsidiary?.toLowerCase();
    }
    async getSubsidiaryByTenant() {
        const tenantId = this.TenantService.getTenantPrefixFromUrl();
        if (tenantId) {
            const tenant = await this.TenantService.get(tenantId);
            return tenant?.blipSubsidiary;
        }
    }
}
ChannelsService.$inject = ['ConfigurationsService', 'GatewayStateConfigurationsService', 'ApplicationService2', 'MessagingHubService', 'TenantService'];
